import { ReactElement, useMemo, useState, useEffect } from 'react'
import {
  Vector3,
  Euler,
  Matrix4,
  PlaneGeometry,
  TubeGeometry,
  CatmullRomCurve3,
  DoubleSide,
} from 'three'
import { TextGeometry } from 'three/addons/geometries/TextGeometry.js'
import { FontLoader, Font } from 'three/addons/loaders/FontLoader.js'
import { ThreeEvent } from '@react-three/fiber'
// Default font URL - Helvetica (similar to Arial)
// const DEFAULT_FONT_URL = 'https://threejs.org/examples/fonts/helvetiker_regular.typeface.json'
import arialFontUrl from 'src/assets/fonts/Arial_Regular.typeface.json?url'
import { TEXT_3D_GLYPH_SIZE_FACTOR } from '../ModelElementLabels/constants'
import {
  calculateText3DLineWidth,
  calculateText3DLabelRectangleHeight,
  calculateText3DLabelRectangleWidth,
  calculateTextWidth,
  TEXT_3D_LABEL_MARGIN_WORLD,
  TEXT_3D_LINE_GAP_FACTOR,
} from '../TextSprite/utils'

export interface Text3DThreeProps {
  textLines: string[]
  color: string
  backgroundColor?: string
  borderColor?: string
  borderWidth?: number
  position: Vector3 | [number, number, number]
  scale?: number
  scaleAxis?: 'X' | 'Y'
  textXOrientation?: Vector3
  textYOrientation?: Vector3
  textDepth?: number
  extrudeDepthSymmetrically?: boolean
  useScaleYForSingleLine?: boolean
  opacity?: number
  anchorLocation?: 'center' | 'top' | 'bottom' | 'left' | 'right'
  /** Background/border inset in world units; defaults to `TEXT_3D_LABEL_MARGIN_WORLD`. */
  margin?: number
  /**
   * Single-sided line gap as a fraction of the text size (each line occupies
   * `1 + 2 * gap` text sizes). Defaults to the renderer's 0.2; the PDF plan
   * labels use 0.15.
   */
  singleSidedLineGapFactor?: number
  /** Widens the text lines and background box; defaults to the renderer's 1.4. */
  textWidthScale?: number
  /**
   * Exact size of the background box in world units, margin included, when it must not come
   * from the text (the PDF plan comments size their box by the export's rules). The text block
   * stays centered in it. Defaults to the text block plus `margin`.
   */
  boxSize?: { width: number; height: number }
  /**
   * Scales only the glyph geometry, not the layout. `TextGeometry` renders
   * glyphs about 1.4x the requested size (see `TEXT_3D_GLYPH_SIZE_FACTOR`);
   * pass `1 / 1.4` to make the rendered em match `scale` exactly, like the
   * PDF plan labels. Defaults to 1 (the renderer's historical size).
   */
  glyphSizeFactor?: number
  /**
   * Draws the label on top of the scene instead of letting scene elements in front of it hide it
   * (background, border and text in that order, after the lines drawn on top at render order 999).
   */
  disableDepthTest?: boolean
  onClick?: (e: ThreeEvent<MouseEvent>) => void
}

const DEFAULT_FONT_URL = arialFontUrl

/** Render order of the background of labels drawn on top; border and text follow it. */
const ON_TOP_RENDER_ORDER = 1000

const Text3DThree = ({
  textLines,
  color,
  backgroundColor,
  borderColor,
  borderWidth,
  position,
  scale = 1,
  opacity = 1,
  anchorLocation = 'center',
  textXOrientation = new Vector3(1, 0, 0),
  textYOrientation = new Vector3(0, 1, 0),
  textDepth = 0.0001,
  extrudeDepthSymmetrically = false,
  margin,
  singleSidedLineGapFactor = TEXT_3D_LINE_GAP_FACTOR,
  textWidthScale,
  boxSize,
  glyphSizeFactor = 1,
  disableDepthTest = false,
  onClick,
}: Text3DThreeProps): ReactElement | null => {
  const renderOrder = disableDepthTest ? ON_TOP_RENDER_ORDER : 0
  const depthProps = disableDepthTest ? { depthTest: false, depthWrite: false } : {}
  const textSizeSingleLine = scale
  const numLines = textLines.length
  const marginWorld = margin ?? TEXT_3D_LABEL_MARGIN_WORLD
  const textHeightIncludingMargin = calculateText3DLabelRectangleHeight(
    numLines,
    textSizeSingleLine,
    singleSidedLineGapFactor,
    marginWorld,
  )
  const totalHeightIncludingMargin = boxSize?.height ?? textHeightIncludingMargin
  const textWidths = textLines.map(line =>
    calculateText3DLineWidth(line, textSizeSingleLine, textWidthScale),
  )
  const maxTextWidth = Math.max(...textWidths)

  // Calculate rectangle dimensions for background and border
  const rectangleWidth =
    boxSize?.width ??
    calculateText3DLabelRectangleWidth(textLines, textSizeSingleLine, textWidthScale, marginWorld)
  const rectangleHeight = totalHeightIncludingMargin

  const [font, setFont] = useState<Font | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Load default font on mount
  useEffect(() => {
    const loader = new FontLoader()

    loader.load(
      DEFAULT_FONT_URL,
      loadedFont => {
        setFont(loadedFont)
        setError(null)
      },
      undefined,
      () => {
        setError('Failed to load font')
      },
    )
  }, [])

  const zDir = useMemo(() => {
    return new Vector3().crossVectors(textXOrientation, textYOrientation).normalize()
  }, [textXOrientation, textYOrientation])

  // Calculate rotation from textXOrientation and textYOrientation
  const rotation = useMemo(() => {
    // Normalize the orientation vectors
    const xDir = textXOrientation.clone().normalize()
    const yDir = textYOrientation.clone().normalize()

    // Create rotation matrix from basis vectors
    // When rotation is 0, textXOrientation corresponds to ex (1,0,0) and textYOrientation to ey (0,1,0)
    const rotationMatrix = new Matrix4().makeBasis(xDir, yDir, zDir)

    // Extract Euler angles from the rotation matrix
    const euler = new Euler().setFromRotationMatrix(rotationMatrix)

    return euler
  }, [textXOrientation, textYOrientation, zDir])

  // Create geometries for each text line
  const geometries = useMemo(() => {
    if (!font || textLines.length === 0) return []

    return textLines.map(text => {
      const geometry = new TextGeometry(text, {
        font,
        size: textSizeSingleLine * glyphSizeFactor,
        curveSegments: 12,
        bevelEnabled: false,
        depth: textDepth,
      })
      return geometry
    })
  }, [font, textLines, textSizeSingleLine, textDepth, glyphSizeFactor])

  // Dispose geometries on unmount
  useEffect(() => {
    return () => {
      geometries.forEach(geometry => {
        geometry.dispose()
      })
    }
  }, [geometries])

  // Create background plane geometry
  const backgroundGeometry = useMemo(() => {
    if (!backgroundColor) return null
    return new PlaneGeometry(rectangleWidth, rectangleHeight)
  }, [backgroundColor, rectangleWidth, rectangleHeight])

  // Dispose background geometry on unmount
  useEffect(() => {
    return () => {
      if (backgroundGeometry) {
        backgroundGeometry.dispose()
      }
    }
  }, [backgroundGeometry])

  // Create border tube geometries for rectangle frame (4 tubes)
  const borderTubes = useMemo(() => {
    if (!borderColor || !borderWidth || borderWidth <= 0) return null

    const halfWidth = rectangleWidth / 2
    const halfHeight = rectangleHeight / 2

    // Calculate tube radius (scaled from borderWidth)
    const tubeRadius = borderWidth / 2

    // Create 4 tube geometries: top, right, bottom, left
    // Each tube is created from a curve with 2 points (straight line)
    const topCurve = new CatmullRomCurve3([
      new Vector3(-halfWidth, halfHeight, 0),
      new Vector3(halfWidth, halfHeight, 0),
    ])
    const topTube = new TubeGeometry(topCurve, 20, tubeRadius, 8, false)

    const rightCurve = new CatmullRomCurve3([
      new Vector3(halfWidth, halfHeight, 0),
      new Vector3(halfWidth, -halfHeight, 0),
    ])
    const rightTube = new TubeGeometry(rightCurve, 20, tubeRadius, 8, false)

    const bottomCurve = new CatmullRomCurve3([
      new Vector3(halfWidth, -halfHeight, 0),
      new Vector3(-halfWidth, -halfHeight, 0),
    ])
    const bottomTube = new TubeGeometry(bottomCurve, 20, tubeRadius, 8, false)

    const leftCurve = new CatmullRomCurve3([
      new Vector3(-halfWidth, -halfHeight, 0),
      new Vector3(-halfWidth, halfHeight, 0),
    ])
    const leftTube = new TubeGeometry(leftCurve, 20, tubeRadius, 8, false)

    return { topTube, rightTube, bottomTube, leftTube }
  }, [borderColor, borderWidth, rectangleWidth, rectangleHeight])

  // Dispose border tube geometries on unmount
  useEffect(() => {
    return () => {
      if (borderTubes) {
        Object.values(borderTubes).forEach(geometry => {
          geometry.dispose()
        })
      }
    }
  }, [borderTubes])

  // In text space the widest line starts at x = 0, so the box (text block plus
  // margin) spans [-margin, maxTextWidth + margin]. Shift it so the anchored
  // edge or center lands exactly on `position`.
  let xAnchorOffset = 0
  switch (anchorLocation) {
    case 'left':
      xAnchorOffset = marginWorld
      break
    case 'right':
      xAnchorOffset = -maxTextWidth - marginWorld
      break
    case 'center':
      xAnchorOffset = -maxTextWidth / 2
      break
    case 'top':
      // its actually top center
      xAnchorOffset = -maxTextWidth / 2
      break
    case 'bottom':
      // its actually bottom center
      xAnchorOffset = -maxTextWidth / 2
      break
    default:
      xAnchorOffset = 0
  }
  let yAnchorOffset = 0
  switch (anchorLocation) {
    case 'top':
      yAnchorOffset = 0
      break
    case 'bottom':
      yAnchorOffset = totalHeightIncludingMargin
      break
    case 'center':
      yAnchorOffset = totalHeightIncludingMargin / 2
      break
    case 'left':
      // its center left
      yAnchorOffset = totalHeightIncludingMargin / 2
      break
    case 'right':
      // its center right
      yAnchorOffset = totalHeightIncludingMargin / 2
      break
    default:
      yAnchorOffset = 0
  }

  // Center every line in the box (like the PDF's text-align center). The
  // glyphs render `TEXT_3D_GLYPH_SIZE_FACTOR * glyphSizeFactor` times the
  // nominal Arial width, which can differ from the (scaled) layout width.
  const linePositions = useMemo(() => {
    const lineHeight = textSizeSingleLine * (1 + singleSidedLineGapFactor * 2)
    const lineGap = textSizeSingleLine * singleSidedLineGapFactor

    // a box taller than the text keeps the text block centered
    const startOffset =
      -lineGap -
      marginWorld -
      textSizeSingleLine -
      (totalHeightIncludingMargin - textHeightIncludingMargin) / 2

    return textLines.map((line, index) => {
      const renderedLineWidth =
        calculateTextWidth(line, textSizeSingleLine) * TEXT_3D_GLYPH_SIZE_FACTOR * glyphSizeFactor
      const deltaX = maxTextWidth - renderedLineWidth
      const yOffset = startOffset - index * lineHeight
      return new Vector3(deltaX / 2, yOffset, 0)
    })
  }, [
    textLines,
    textSizeSingleLine,
    glyphSizeFactor,
    maxTextWidth,
    singleSidedLineGapFactor,
    marginWorld,
    totalHeightIncludingMargin,
    textHeightIncludingMargin,
  ])

  // Calculate background and border positions
  const backgroundPosition = useMemo(() => {
    // Center of the box in text space: the widest line spans [0, maxTextWidth]
    // and the margin is symmetric, so the box is centered on the text block.
    return new Vector3(
      maxTextWidth / 2,
      -totalHeightIncludingMargin / 2,
      extrudeDepthSymmetrically ? textDepth / 2 : 0,
    )
  }, [totalHeightIncludingMargin, maxTextWidth, textDepth, extrudeDepthSymmetrically])

  const borderPosition = backgroundPosition

  // Convert position to Vector3 if needed
  const positionVector = useMemo(() => {
    if (Array.isArray(position)) {
      return new Vector3(...position)
    }
    return position.clone()
  }, [position])

  const anchorOffset = new Vector3(xAnchorOffset, yAnchorOffset, 0)

  // Don't render if font is not loaded, there's an error, or no text lines
  if (error || !font || geometries.length === 0) {
    return null
  }

  return (
    <group>
      <group
        position={positionVector
          .clone()
          .sub(zDir.clone().multiplyScalar(extrudeDepthSymmetrically ? textDepth / 2 : textDepth))}
      >
        {/* Apply rotation to the entire group */}
        <group rotation={rotation}>
          <group position={anchorOffset}>
            {/* Background plane */}
            {backgroundGeometry && (
              <mesh
                position={backgroundPosition}
                geometry={backgroundGeometry}
                renderOrder={renderOrder}
                onClick={onClick}
              >
                <meshBasicMaterial
                  color={backgroundColor}
                  opacity={opacity}
                  transparent={true}
                  side={DoubleSide}
                  {...depthProps}
                />
              </mesh>
            )}

            {/* Border tube frame */}
            {borderTubes && borderPosition && (
              <group position={borderPosition}>
                <mesh
                  geometry={borderTubes.topTube}
                  renderOrder={renderOrder + 1}
                  onClick={onClick}
                >
                  <meshBasicMaterial
                    color={borderColor}
                    opacity={opacity}
                    transparent={true}
                    side={DoubleSide}
                    {...depthProps}
                  />
                </mesh>
                <mesh
                  geometry={borderTubes.rightTube}
                  renderOrder={renderOrder + 1}
                  onClick={onClick}
                >
                  <meshBasicMaterial
                    color={borderColor}
                    opacity={opacity}
                    transparent={true}
                    side={DoubleSide}
                    {...depthProps}
                  />
                </mesh>
                <mesh
                  geometry={borderTubes.bottomTube}
                  renderOrder={renderOrder + 1}
                  onClick={onClick}
                >
                  <meshBasicMaterial
                    color={borderColor}
                    opacity={opacity}
                    transparent={true}
                    side={DoubleSide}
                    {...depthProps}
                  />
                </mesh>
                <mesh
                  geometry={borderTubes.leftTube}
                  renderOrder={renderOrder + 1}
                  onClick={onClick}
                >
                  <meshBasicMaterial
                    color={borderColor}
                    opacity={opacity}
                    transparent={true}
                    side={DoubleSide}
                    {...depthProps}
                  />
                </mesh>
              </group>
            )}

            {/* Text meshes */}
            {geometries.map((geometry, index) => (
              <mesh
                key={index}
                position={linePositions[index]}
                renderOrder={renderOrder + 2}
                onClick={onClick}
              >
                <primitive object={geometry} attach="geometry" />
                <meshBasicMaterial
                  color={color}
                  opacity={opacity}
                  transparent={true}
                  side={DoubleSide}
                  {...depthProps}
                />
              </mesh>
            ))}
          </group>
        </group>
      </group>
    </group>
  )
}

export default Text3DThree
